import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Register() {
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.last_name.trim() || !f.email.trim()) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    if (f.password !== f.confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/register", {
        first_name: f.first_name, last_name: f.last_name, email: f.email, password: f.password,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputClass = "w-full h-12 px-4 rounded-xl bg-slate-50/60 border border-slate-200 text-slate-900 placeholder:text-slate-400 outline-none focus:bg-white focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 dark:bg-white/5 dark:border-white/10 dark:text-slate-100";

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#FAFAFA] p-4 dark:bg-[#0A0A0A]">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-[460px] rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:border-white/10 dark:bg-white/[0.04]"
        data-testid="register-card"
      >
        <div className="mb-6 flex items-center gap-2.5">
          <Logo size={34} />
          <span className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">Le Passeport</span>
        </div>

        {done ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="register-success">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-500/10">
              <MailCheck size={30} />
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Vérifiez votre e-mail</h1>
            <p className="text-sm text-slate-500">
              Un e-mail de validation a été envoyé à <strong>{f.email}</strong>. Cliquez sur le lien pour activer votre compte, puis connectez-vous.
            </p>
            <button onClick={() => navigate("/login")} className="mt-2 text-sm font-semibold text-slate-900 hover:underline dark:text-slate-100" data-testid="register-to-login">
              Retour à la connexion
            </button>
          </div>
        ) : (
          <>
            <button onClick={() => navigate("/login")} className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600" data-testid="register-back">
              <ArrowLeft size={15} /> Connexion
            </button>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Créer un compte enseignant</h1>
            <p className="mt-1 mb-5 text-sm text-slate-500">Votre espace personnel, indépendant et sécurisé.</p>
            <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
              <div className="grid grid-cols-2 gap-3">
                <input className={inputClass} placeholder="Prénom" value={f.first_name} onChange={upd("first_name")} data-testid="register-first-name" />
                <input className={inputClass} placeholder="Nom" value={f.last_name} onChange={upd("last_name")} data-testid="register-last-name" />
              </div>
              <input className={inputClass} type="email" autoComplete="email" placeholder="Adresse e-mail" value={f.email} onChange={upd("email")} data-testid="register-email" />
              <input className={inputClass} type="password" placeholder="Mot de passe (8 caractères min., 1 lettre + 1 chiffre)" value={f.password} onChange={upd("password")} data-testid="register-password" />
              <input className={inputClass} type="password" placeholder="Confirmer le mot de passe" value={f.confirm} onChange={upd("confirm")} data-testid="register-confirm" />
              <button type="submit" disabled={busy} className="mt-2 flex h-12 w-full items-center justify-center rounded-xl bg-slate-900 font-medium text-white transition-transform hover:scale-[0.98] disabled:opacity-60 dark:bg-white dark:text-slate-900" data-testid="register-submit">
                {busy ? <Loader2 size={20} className="animate-spin" /> : "Créer mon compte"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
